import { apiGet, apiPost } from '@/auth/api-client';
import { getPendingOperations, getConflictOperations, removeOperations, markOperationStatus, } from '@/storage/outbox';
import { loadBudgets, saveBudgets } from '@/storage/storage';
import { getSyncCursor, setSyncCursor, setLastSyncTime, getServerDeviceId, } from '@/storage/sync-meta';
import { applyRemoteToBudgets } from './remote-apply';
/** Причины reject, которые НЕ надо переотправлять — клиентское состояние невалидно. */
const PERMANENT_REJECT_REASONS = new Set([
    'OwnershipViolation',
    'LocalOnlyBudget',
    // Битая операция (например, null в обязательном поле) — сервер её не примет никогда,
    // выкидываем из outbox, иначе висит вечно и мешает.
    'MalformedOperation',
]);
/** Reject из-за неактивной подписки — операцию оставляем pending до реактивации. */
const TRANSIENT_REJECT_REASONS = new Set(['CloudSyncDisabled']);
function capitalize(s) {
    return s.charAt(0).toUpperCase() + s.slice(1);
}
export class SyncEngine {
    constructor() {
        this._status = {
            state: 'idle',
            lastSyncAt: null,
            pendingCount: 0,
            conflictCount: 0,
            error: null,
        };
        this._listeners = new Set();
        this._syncing = false;
        this._onDataChanged = null;
    }
    /** Register a callback that fires after pull applies remote changes to IDB. */
    setOnDataChanged(cb) {
        this._onDataChanged = cb;
    }
    subscribe(listener) {
        this._listeners.add(listener);
        return () => this._listeners.delete(listener);
    }
    getStatus() {
        return this._status;
    }
    /**
     * Пересчитывает pendingCount и conflictCount из ФАКТИЧЕСКОГО outbox и оповещает
     * подписчиков. Счётчики — производные от outbox, а не «липкое» in-memory
     * состояние: иначе бэдж горит зелёным при непустом outbox (pending не обновлён
     * до push) либо оранжевым при пустом (conflictCount не сброшен после конфликта).
     */
    async refreshCounts() {
        try {
            const [pending, conflicts] = await Promise.all([
                getPendingOperations(),
                getConflictOperations(),
            ]);
            this.notify({ pendingCount: pending.length, conflictCount: conflicts.length });
        }
        catch {
            // не критично — счётчики обновятся на следующем событии
        }
    }
    notify(partial) {
        this._status = { ...this._status, ...partial };
        for (const l of this._listeners)
            l(this._status);
    }
    async triggerSync() {
        if (this._syncing)
            return;
        this._syncing = true;
        try {
            this.notify({ state: 'pushing', error: null });
            await this.push();
            this.notify({ state: 'pulling' });
            await this.pull();
            // Сверяем локальные conflict-записи с серверной правдой: осиротевшие
            // (нет в GET /sync/conflicts) — чистим, чтобы бэдж не залипал числом,
            // которого нет в модалке.
            await this.reconcileConflicts();
            const now = new Date().toISOString();
            await setLastSyncTime(now);
            // Счётчики берём из фактического outbox (сбрасывает залипший conflictCount)
            await this.refreshCounts();
            this.notify({ state: 'idle', lastSyncAt: now });
        }
        catch (err) {
            this.notify({ state: 'error', error: String(err) });
        }
        finally {
            this._syncing = false;
        }
    }
    async push() {
        const pending = await getPendingOperations();
        if (pending.length === 0)
            return;
        const collapsed = this.collapseOperations(pending);
        const deviceId = await getServerDeviceId();
        if (!deviceId)
            throw new Error('Server device ID not available. Bootstrap may not have completed.');
        const cursor = await getSyncCursor();
        const body = {
            deviceId,
            knownCursor: cursor,
            operations: collapsed.map((op) => ({
                opId: op.opId,
                entityType: capitalize(op.entityType),
                entityId: op.entityId,
                budgetId: op.budgetId,
                operationType: capitalize(op.operationType),
                baseVersion: op.baseVersion,
                payload: op.payload,
                clientTimestampUtc: op.clientTimestampUtc,
            })),
        };
        const response = await apiPost('/api/v1/sync/push', body);
        const acceptedIds = response.accepted.map((a) => a.opId);
        const conflictIds = response.conflicts.map((c) => c.opId);
        if (acceptedIds.length > 0)
            await removeOperations(acceptedIds);
        for (const id of conflictIds)
            await markOperationStatus(id, 'conflict');
        // rejected делим по причине:
        //  - permanent (Ownership/LocalOnly) — состояние невалидно, выкидываем из outbox;
        //  - transient (CloudSyncDisabled) — оставляем pending, уедет после реактивации подписки;
        //  - прочее — метим 'rejected' (surface, ручная чистка).
        const permanentRejects = [];
        for (const r of response.rejected) {
            if (PERMANENT_REJECT_REASONS.has(r.reason))
                permanentRejects.push(r.opId);
            else if (TRANSIENT_REJECT_REASONS.has(r.reason))
                continue; // keep pending
            else
                await markOperationStatus(r.opId, 'rejected');
        }
        if (permanentRejects.length > 0)
            await removeOperations(permanentRejects);
        // Убираем залежавшиеся conflict-записи по сущностям, которые только что были
        // приняты свежей операцией — иначе позднее resolve затрёт более новую правку.
        if (acceptedIds.length > 0)
            await this.cleanupSupersededConflicts(collapsed, acceptedIds);
        if (response.newCursor)
            await setSyncCursor(response.newCursor);
        // Счётчики — из фактического outbox: conflict/rejected остаются в нём со своим
        // статусом, поэтому pending.length - accepted переоценивает pending.
        await this.refreshCounts();
    }
    async pull() {
        let hasMore = true;
        while (hasMore) {
            const cursor = await getSyncCursor();
            const response = await apiGet(`/api/v1/sync/pull?cursor=${cursor}&limit=100`);
            await this.applyRemoteChanges(response.upserts, response.deletions);
            await setSyncCursor(response.cursor);
            hasMore = response.hasMore;
        }
        // Notify that IDB data changed so React state can reload
        this._onDataChanged?.();
    }
    async applyRemoteChanges(upserts, deletions) {
        const budgets = await loadBudgets();
        const result = applyRemoteToBudgets(budgets, upserts, deletions);
        await saveBudgets(result);
    }
    /** Список незакрытых конфликтов (обе версии) для панели ручного разрешения. */
    async fetchConflicts() {
        return apiGet('/api/v1/sync/conflicts');
    }
    /**
     * Реконсиляция локальных conflict-записей с серверной правдой. Бэдж считает
     * conflictCount по локальным outbox-записям status='conflict', а модалка читает
     * серверный GET /sync/conflicts. Записи, которых сервер НЕ подтверждает (осиротевшие
     * от старого кода или ошибочно помеченные) — удаляем, иначе число залипает навсегда.
     * Вызывается только после успешного sync (сервер = источник истины).
     */
    async reconcileConflicts() {
        const localConflicts = await getConflictOperations();
        if (localConflicts.length === 0)
            return;
        const serverConflicts = await this.fetchConflicts();
        // Сервер шлёт EntityType с большой буквы ('Budget'), локально — lowercase.
        const serverKeys = new Set(serverConflicts.map((c) => `${c.entityType.toLowerCase()}:${c.entityId}`));
        const orphaned = localConflicts
            .filter((c) => !serverKeys.has(`${c.entityType}:${c.entityId}`))
            .map((c) => c.opId);
        if (orphaned.length > 0)
            await removeOperations(orphaned);
    }
    /**
     * Разрешает один конфликт: KeepMine (force-apply клиентской версии) или KeepServer
     * (принять серверную). После ответа убирает conflict-записи этой сущности из outbox.
     * Синхронизацию (pull новой версии + пересчёт счётчиков) вызывающий делает батчем
     * через triggerSync() после разрешения всех конфликтов.
     */
    async resolveConflict(entityType, entityId, choice) {
        const deviceId = await getServerDeviceId();
        if (!deviceId)
            throw new Error('Server device ID not available.');
        await apiPost(`/api/v1/sync/conflicts/${entityType}/${entityId}/resolve`, {
            deviceId,
            choice,
        });
        const lowerType = entityType.toLowerCase();
        const conflicts = await getConflictOperations();
        const toRemove = conflicts
            .filter((c) => c.entityType === lowerType && c.entityId === entityId)
            .map((c) => c.opId);
        if (toRemove.length > 0)
            await removeOperations(toRemove);
        await this.refreshCounts();
    }
    async cleanupSupersededConflicts(collapsed, acceptedIds) {
        const acceptedSet = new Set(acceptedIds);
        const acceptedKeys = new Set(collapsed
            .filter((op) => acceptedSet.has(op.opId))
            .map((op) => `${op.entityType}:${op.entityId}`));
        if (acceptedKeys.size === 0)
            return;
        const conflicts = await getConflictOperations();
        const stale = conflicts
            .filter((c) => acceptedKeys.has(`${c.entityType}:${c.entityId}`))
            .map((c) => c.opId);
        if (stale.length > 0)
            await removeOperations(stale);
    }
    collapseOperations(ops) {
        const map = new Map();
        for (const op of ops) {
            const key = `${op.entityType}:${op.entityId}`;
            const existing = map.get(key);
            if (!existing || op.clientTimestampUtc > existing.clientTimestampUtc) {
                map.set(key, op);
            }
        }
        return Array.from(map.values());
    }
}
